/**
 * Copyright (c) VVDN Technologies Pvt Ltd. All rights reserved.
 * Proprietary and confidential — custom algorithms.
 */

import { assignOptimalChannels } from './rfChannels'
import { syncApChannels } from './rfRecommendationFixes'
import { pointInObstacleFootprint } from './obstacles'
import { computeHeatmapGrid, type HeatmapCell } from './rfSimulation'
import { pointInFloorBounds } from './floorBounds'
import { RF_GRID_COLS, RF_GRID_ROWS } from './rfGrid'
import type { FloorGeometry, FloorRoom, PlacedAp } from './types'

export { RF_GRID_COLS, RF_GRID_ROWS }

export type FloorPlanMetrics = {
  weakPct: number
  fairPct: number
  goodPct: number
  highInterfPct: number
  severeInterfPct: number
  avgRssi: number
  score: number
}

export type RequirementZonePass = {
  roomId: string
  name: string
  minRssiDbm: number
  /** null when the zone has no sampleable floor cells (too small or fully blocked). */
  passPct: number | null
  cellCount: number
  passCount: number
}

export type RequirementPassSummary = {
  overallPassPct: number
  zones: RequirementZonePass[]
}

export function optimizationGrid(
  aps: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): HeatmapCell[] {
  return computeHeatmapGrid(
    apsForRfAnalysis(aps, geometry, floorWidthM, floorHeightM),
    geometry,
    floorWidthM,
    floorHeightM,
    RF_GRID_COLS,
    RF_GRID_ROWS,
  )
}

/** AP list with persisted + greedy channel plan so interference metrics are meaningful. */
export function apsForRfAnalysis(
  aps: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): PlacedAp[] {
  let list = syncApChannels(aps.map(a => ({ ...a })), geometry)
  if (list.length >= 2 && list.some(ap => ap.channel == null)) {
    const ch = assignOptimalChannels(list, geometry.bandGHz, floorWidthM, floorHeightM)
    list = list.map(ap => ({ ...ap, channel: ch[ap.id] ?? ap.channel }))
  }
  return list
}

function cellsInRoom(cells: HeatmapCell[], room: FloorRoom): HeatmapCell[] {
  return cells.filter(c => {
    const rx = c.x - room.x
    const ry = c.y - room.y
    return rx >= 0 && ry >= 0 && rx <= room.w && ry <= room.h
  })
}

function serviceableCellsInRoom(
  cells: HeatmapCell[],
  room: FloorRoom,
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
  hasBackgroundImage: boolean,
): HeatmapCell[] {
  return cellsInRoom(cells, room).filter(
    c =>
      pointInFloorBounds(c.x, c.y, geometry, hasBackgroundImage) &&
      !pointInObstacleFootprint(c.x, c.y, geometry.obstacles, floorWidthM, floorHeightM),
  )
}

/** Pass rate for rooms marked with coverageRequirement (min RSSI target). */
export function computeRequirementPassSummary(
  aps: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
  grid?: HeatmapCell[],
  hasBackgroundImage = true,
): RequirementPassSummary | null {
  const zones = geometry.rooms.filter(r => r.coverageRequirement != null)
  if (zones.length === 0) return null

  const allCells = grid ?? optimizationGrid(aps, geometry, floorWidthM, floorHeightM)

  const results: RequirementZonePass[] = zones.map(room => {
    const minRssi = room.coverageRequirement?.minRssiDbm ?? -65
    const inZone = serviceableCellsInRoom(
      allCells,
      room,
      geometry,
      floorWidthM,
      floorHeightM,
      hasBackgroundImage,
    )
    if (inZone.length === 0) {
      return {
        roomId: room.id,
        name: room.name,
        minRssiDbm: minRssi,
        passPct: null,
        cellCount: 0,
        passCount: 0,
      }
    }
    const passCount = inZone.filter(c => c.rssi >= minRssi).length
    return {
      roomId: room.id,
      name: room.name,
      minRssiDbm: minRssi,
      passPct: (passCount / inZone.length) * 100,
      cellCount: inZone.length,
      passCount,
    }
  })

  const scored = results.filter(z => z.cellCount > 0 && z.passPct != null)
  const overallPassPct =
    scored.length > 0 ? scored.reduce((s, z) => s + (z.passPct ?? 0), 0) / scored.length : 0

  return { overallPassPct, zones: results }
}

export function computeFloorMetrics(
  aps: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
  grid?: HeatmapCell[],
  hasBackgroundImage = true,
): FloorPlanMetrics {
  const allCells = grid ?? optimizationGrid(aps, geometry, floorWidthM, floorHeightM)
  const cells = allCells.filter(c => pointInFloorBounds(c.x, c.y, geometry, hasBackgroundImage))
  const total = Math.max(1, cells.length)
  const weak = cells.filter(c => c.rssi < -75).length
  const fair = cells.filter(c => c.rssi >= -75 && c.rssi < -65).length
  const good = cells.filter(c => c.rssi >= -65).length
  const highInterf = cells.filter(c => c.interference >= 55).length
  const severeInterf = cells.filter(c => c.interference >= 75).length
  const avgRssi = cells.reduce((s, c) => s + c.rssi, 0) / total

  const weakPct = (weak / total) * 100
  const fairPct = (fair / total) * 100
  const goodPct = (good / total) * 100
  const highInterfPct = (highInterf / total) * 100
  const severeInterfPct = (severeInterf / total) * 100

  const score =
    goodPct * 0.45 +
    fairPct * 0.2 -
    weakPct * 0.55 -
    highInterfPct * 0.35 -
    severeInterfPct * 0.5 +
    Math.min(10, avgRssi + 70)

  return { weakPct, fairPct, goodPct, highInterfPct, severeInterfPct, avgRssi, score }
}
